import React, { useRef, useState, useEffect } from "react";

interface TextEditorProps {
  value: string; // Initial content for the editor
  onChange: (content: string) => void; // Callback to handle content changes
}

const TextEditor: React.FC<TextEditorProps> = ({ value, onChange }) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const isFormattingRef = useRef(false); // Track whether a formatting action is happening
  const [showModal, setShowModal] = useState(false); // State to show/hide modal
  const [linkUrl, setLinkUrl] = useState(""); // State to store link URL
  const [openInNewTab, setOpenInNewTab] = useState(false); // State for "open in new tab"
  const savedSelection = useRef<Range | null>(null); // Save the user's selection

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      // Only update the editor's content if it's different from the current state
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  const handleInput = () => {
    if (editorRef.current) {
      const content = editorRef.current.innerHTML;
      if (content !== value) {
        onChange(content); // Notify parent about changes
      }
    }
  };

  const saveSelection = () => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      savedSelection.current = selection.getRangeAt(0); // Save the selection
    }
  };

  const restoreSelection = () => {
    const selection = window.getSelection();
    if (savedSelection.current && selection) {
      selection.removeAllRanges(); // Clear existing selections
      selection.addRange(savedSelection.current); // Restore saved selection
    }
  };

  const formatText = (command: string, value?: string) => {
    isFormattingRef.current = true;
    document.execCommand(command, false, value);
    if (editorRef.current) {
      const content = editorRef.current.innerHTML;
      onChange(content);
    }
  };

  const handleAddLink = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!linkUrl.trim()) {
      alert("Please enter a valid URL.");
      return;
    }

    restoreSelection(); // Restore the saved selection before adding the link

    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const linkElement = document.createElement("a");
      linkElement.href = linkUrl.trim();
      if (openInNewTab) {
        linkElement.target = "_blank";
        linkElement.rel = "noopener noreferrer";
      }
      linkElement.textContent = range.toString(); // Use selected text
      range.deleteContents(); // Remove the original text
      range.insertNode(linkElement); // Insert the link element
    }

    setShowModal(false); // Close modal
    setLinkUrl(""); // Reset URL field
    setOpenInNewTab(false); // Reset checkbox

    if (editorRef.current) {
      const content = editorRef.current.innerHTML;
      onChange(content); // Update content in parent
    }
  };

  const handleRemoveLink = () => {
    formatText("unlink");
  };

  return (
    <div>
      {/* Toolbar */}
      <div className="d-flex mb-3 gap-2">
        <button
          className="btn btn-outline-primary btn-lg d-flex justify-content-center align-items-center"
          title="Bold"
          onClick={(e) => {
            e.preventDefault();
            formatText("bold");
          }}
        >
          <i className="bi bi-type-bold"></i>
        </button>
        <button
          className="btn btn-outline-secondary btn-lg d-flex justify-content-center align-items-center"
          title="Italic"
          onClick={(e) => {
            e.preventDefault();
            formatText("italic");
          }}
        >
          <i className="bi bi-type-italic"></i>
        </button>
        <button
          className="btn btn-outline-success btn-lg d-flex justify-content-center align-items-center"
          title="Underline"
          onClick={(e) => {
            e.preventDefault();
            formatText("underline");
          }}
        >
          <i className="bi bi-type-underline"></i>
        </button>
        <button
          className="btn btn-outline-info btn-lg d-flex justify-content-center align-items-center"
          title="Ordered List"
          onClick={(e) => {
            e.preventDefault();
            formatText("insertOrderedList");
          }}
        >
          <i className="bi bi-list-ol"></i>
        </button>
        <button
          className="btn btn-outline-warning btn-lg d-flex justify-content-center align-items-center"
          title="Unordered List"
          onClick={(e) => {
            e.preventDefault();
            formatText("insertUnorderedList");
          }}
        >
          <i className="bi bi-list-ul"></i>
        </button>
        <button
          className="btn btn-outline-dark"
          onClick={(e) => {
            e.preventDefault();
            saveSelection(); // Save the current selection before opening the modal
            setShowModal(true);
          }}
        >
          Add Link
        </button>
        <button
          className="btn btn-outline-danger"
          onClick={(e) => {
            e.preventDefault();
            handleRemoveLink();
          }}
        >
          Remove Link
        </button>
      </div>

      {/* Editable Div */}
      <div
        ref={editorRef}
        contentEditable
        className="form-control"
        style={{
          minHeight: "150px",
          border: "1px solid #ccc",
          padding: "10px",
          borderRadius: "5px",
          marginBottom: "20px",
          textAlign: "left", // Ensure text aligns to the right
        
        }}
        onInput={handleInput}
        suppressContentEditableWarning={true}
      />

      {/* Modal */}
      {showModal && (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "white",
            border: "1px solid #ccc",
            borderRadius: "8px",
            padding: "20px",
            zIndex: 1000,
          }}
        >
          <h4>Add Link</h4>
          <div>
            <input
              type="text"
              className="form-control mb-2"
              placeholder="Enter URL"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
            />
            <div className="form-check mb-2">
              <input
                type="checkbox"
                className="form-check-input"
                id="newTab"
                checked={openInNewTab}
                onChange={(e) => setOpenInNewTab(e.target.checked)}
              />
              <label htmlFor="newTab" className="form-check-label">
                Open in new tab
              </label>
            </div>
            <button type="button" className="btn btn-primary me-2" onClick={handleAddLink}>
              Add Link
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={(e) => {
                e.preventDefault();
                setShowModal(false);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Overlay to close modal */}
      {showModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 999,
          }}
          onClick={() => setShowModal(false)}
        ></div>
      )}
    </div>
  );
};

export default TextEditor;
